/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import {
  LangSelector,
  ToastBanner,
  useRenderErrorReport,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { ComponentChildren, VNode, h } from "preact";
import logo from "../../../../contrib/taler-assets/svg/logo/taler-logo-dark.svg";
import { VerificationCard } from "../components/VerificationUi.js";

const GIT_HASH = typeof __GIT_HASH__ !== "undefined" ? __GIT_HASH__ : "unknown";
const VERSION = typeof __VERSION__ !== "undefined" ? __VERSION__ : "unknown";

export function Frame({ children }: { children: ComponentChildren }): VNode {
  const { i18n } = useTranslationContext();
  // Translators: Product-area label in the header of every Challenger page.
  const productAreaLabel = i18n.str`Identity verification`;
  // Translators: Generic page title used after an unexpected rendering error.
  const unexpectedErrorTitle = i18n.str`Something went wrong`;
  // Translators: Recovery instruction shown after an unexpected web UI error.
  const unexpectedErrorDescription = i18n.str`Reload this page. If the problem continues, return to the application that started the verification.`;
  const failed = useRenderErrorReport({
    hash: GIT_HASH,
    version: VERSION,
  });

  return (
    <div class="flex min-h-screen flex-col bg-secondaryContainer text-onBackground dark:bg-darkBackground dark:text-darkOnBackground">
      <header class="border-b border-primary bg-primary text-onPrimary shadow-sm dark:border-darkPrimary dark:bg-darkPrimaryContainer dark:text-darkOnPrimaryContainer">
        <div class="mx-auto flex h-16 w-full max-w-5xl items-center justify-between px-4 sm:px-6">
          <div class="flex min-w-0 items-center gap-3">
            <img class="h-9 w-auto" src={logo} alt="GNU Taler" />
            <span class="truncate border-l border-onPrimary/40 pl-3 text-sm font-semibold sm:text-base">
              {productAreaLabel}
            </span>
          </div>
          <div class="relative">
            <LangSelector type="icon" />
          </div>
        </div>
      </header>

      <main class="flex flex-1 flex-col items-center px-4 py-6 sm:px-6 sm:py-12">
        <div class="mb-3 w-full max-w-xl">
          <ToastBanner />
        </div>
        {!failed ? (
          children
        ) : (
          <VerificationCard title={unexpectedErrorTitle}>
            <p class="text-center text-secondary dark:text-darkSecondary">
              {unexpectedErrorDescription}
            </p>
          </VerificationCard>
        )}
      </main>
    </div>
  );
}
